// (c) chaehongee. Confidential and proprietary.

import type { ReactElement } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import styles from '../Backstage.module.css'

interface ModelDropdownProps {
    value: string
    disabled?: boolean
    onChange: (value: string) => void
}

const OPTIONS: Array<{ value: string; label: string; hint: string }> = [
    { value: 'auto', label: 'Auto', hint: 'Agent picks the best model' },
    { value: 'gpt_image_generate', label: 'GPT Image 2', hint: 'Complex scenes, highest fidelity' },
    { value: 'nano_banana_pro_edit', label: 'Nano Banana Pro', hint: 'Best editing, fast' },
    { value: 'nano_banana_text_to_image', label: 'Nano Banana 2', hint: 'Fast, accurate' },
    { value: 'flux2_pro_text_to_image', label: 'FLUX.2 Pro', hint: 'Photorealism, speed' },
    { value: 'seedream_text_to_image', label: 'Seedream 4.5', hint: 'Text in images, Korean' },
    { value: 'grok_imagine', label: 'Grok Imagine', hint: 'Quick drafts, low cost' },
    { value: 'recraft_v4_generate', label: 'Recraft V4', hint: 'Design, logos, illustration' },
    { value: 'text_to_image', label: 'Imagen 4', hint: 'General purpose' },
    { value: 'flux_schnell_text_to_image', label: 'FLUX Schnell', hint: 'Fastest drafts' },
]

export function ModelDropdown(props: ModelDropdownProps): ReactElement {
    const { value, disabled = false, onChange } = props
    const [open, setOpen] = useState(false)
    const [anchor, setAnchor] = useState<{ x: number; y: number; width: number } | null>(null)
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const menuRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!open) return
        const handlePointerDown = (e: PointerEvent) => {
            const target = e.target as Node | null
            if (menuRef.current && target && menuRef.current.contains(target)) return
            if (buttonRef.current && target && buttonRef.current.contains(target)) return
            setOpen(false)
        }
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
        }
        window.addEventListener('pointerdown', handlePointerDown, { capture: true })
        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('pointerdown', handlePointerDown, { capture: true } as any)
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [open])

    const openMenu = () => {
        if (disabled) return
        const rect = buttonRef.current?.getBoundingClientRect()
        if (!rect) return
        setAnchor({ x: rect.left, y: rect.top - 6, width: Math.max(rect.width, 200) })
        setOpen(true)
    }

    const toggleMenu = () => {
        if (open) {
            setOpen(false)
        } else {
            openMenu()
        }
    }

    const selectedLabel =
        OPTIONS.find((opt) => opt.value === value)?.label ?? 'Auto'

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className={styles.aspectRatioTrigger}
                onClick={toggleMenu}
                disabled={disabled}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label="Model"
            >
                <span className={styles.aspectRatioTriggerValue}>{selectedLabel}</span>
                <ChevronDown className={styles.aspectRatioTriggerIcon} aria-hidden="true" />
            </button>
            {open && anchor && (
                <div
                    ref={menuRef}
                    className={`${styles.dropdownMenu} ${styles.dropdownMenuUp}`}
                    style={{ left: anchor.x, top: anchor.y, minWidth: anchor.width }}
                    role="menu"
                >
                    {OPTIONS.map((opt) => {
                        const isSelected = opt.value === value
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                className={styles.dropdownItem}
                                data-selected={isSelected}
                                onClick={() => {
                                    onChange(opt.value)
                                    setOpen(false)
                                }}
                                role="menuitemradio"
                                aria-checked={isSelected}
                            >
                                <span className={styles.dropdownItemLabel}>{opt.label}</span>
                                <span className={styles.modelDropdownHint}>{opt.hint}</span>
                                {isSelected ? (
                                    <Check className={styles.dropdownItemCheck} aria-hidden="true" />
                                ) : null}
                            </button>
                        )
                    })}
                </div>
            )}
        </>
    )
}
